import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import type { MeResponse } from '@sparkery/contracts';

// Load order is the only precedence mechanism in this codebase.
// 1 — tokens (:root only)          2 — shared element families
import '@sparkery/tokens/themes.css';
import '@sparkery/ui/styles.css';
// 4 — view/layout scoped css is imported by each route; 3 — by each component.
import { ApiError, apiGet } from './lib/api.ts';
import { PortalAuthProvider, usePortalAuth } from './lib/auth.tsx';
import { ViewerProvider } from './lib/viewer.tsx';
import { buildRouter } from './routes/router.tsx';
import { SignInRequired, StartupError, StartingUp } from './routes/StartupStates.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // GA4 and Shopify figures are refreshed on a schedule, not per request, so
      // hammering the API on every window focus buys nothing.
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      retry: (attempt, error) =>
        !(error instanceof ApiError && (error.isUnauthorized || error.isForbidden)) && attempt < 2,
    },
  },
});

type StartupPreview = 'signedInAs' | 'startingUp' | 'startupError';

function readStartupPreview(): StartupPreview | null {
  if (!import.meta.env.DEV) return null;

  switch (window.location.hash) {
    case '#signedInAs':
      return 'signedInAs';
    case '#startingUp':
      return 'startingUp';
    case '#startupError':
      return 'startupError';
    default:
      return null;
  }
}

function useStartupPreview(): StartupPreview | null {
  const [preview, setPreview] = useState(readStartupPreview);

  useEffect(() => {
    const sync = () => setPreview(readStartupPreview());
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);

  return preview;
}

/**
 * The gate between "there is a session" and "the app can render".
 *
 * `/v1/me` used to be fetched before React mounted at all. It cannot be any more:
 * AuthKit resolves a session inside React — from the redirect URL or from storage — so
 * the token does not exist until it has run. The order is now
 *
 *   AuthKit resolves → the fetch layer has a token → /me → the router mounts
 *
 * and no route renders before the viewer exists, which is what the old order bought and
 * what the guards still rely on.
 */
function AuthGate() {
  const startupPreview = useStartupPreview();
  const auth = usePortalAuth();

  const me = useQuery({
    queryKey: ['me', auth.sessionKey],
    queryFn: () => apiGet<MeResponse>('/me'),
    // Not attempted until AuthKit has finished: a request sent early goes out with no
    // bearer token and 401s, which would look like a signed-out session.
    enabled: !auth.isLoading && auth.isAuthenticated,
    retry: false,
    staleTime: Infinity,
  });

  if (startupPreview === 'startingUp') return <StartingUp />;
  if (startupPreview === 'startupError') {
    return <StartupError message="Previewing the startup error state." />;
  }
  if (startupPreview === 'signedInAs') {
    return (
      <SignInRequired
        signedInAs="partner@example.com"
        onSignIn={() => {
          window.location.hash = '';
        }}
      />
    );
  }

  if (auth.isLoading) return <StartingUp />;

  if (!auth.isAuthenticated) {
    return (
      <SignInRequired
        {...(auth.signIn ? { onSignIn: auth.signIn } : {})}
        {...(auth.signInAsAdmin ? { onSignInAsAdmin: auth.signInAsAdmin } : {})}
      />
    );
  }

  if (me.isPending) return <StartingUp />;

  if (me.error) {
    if (me.error instanceof ApiError && me.error.isUnauthorized) {
      // Signed in with the provider, but no seat in this portal — a real user of another
      // Sense product, or a partner whose organisation is not mapped.
      return (
        <SignInRequired
          {...(auth.switchAccount ? { onSignIn: auth.switchAccount } : {})}
          {...(auth.signInAsAdmin ? { onSignInAsAdmin: auth.signInAsAdmin } : {})}
          {...(auth.email ? { signedInAs: auth.email } : {})}
        />
      );
    }
    return <StartupError message={me.error.message} />;
  }

  const router = buildRouter(me.data.viewer);

  return (
    <ViewerProvider me={me.data}>
      <RouterProvider router={router} />
    </ViewerProvider>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('#root missing from index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <PortalAuthProvider>
        <AuthGate />
      </PortalAuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
